// Session timeout utility
// Automatically clears session data after 3 hours (10800000 ms)

const SESSION_TIMEOUT_DURATION = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const SESSION_START_KEY = 'sessionStartTime';
const SESSION_LAST_ACTIVITY_KEY = 'sessionLastActivity';

// Session data keys that should be cleared on timeout
const SESSION_DATA_KEYS = [
  'userDetails',
  'generatedContentData',
  'landingPageContent',
  'publishData',
  'scheduleData',
  'isInstagramConnected',
  'generatedContent',
  'ig_token_valid'
];

/**
 * Initialize session timeout tracking
 * Call this when the app starts or user logs in
 */
export const initializeSessionTimeout = () => {
  const now = Date.now();
  
  // Set session start time if not already set
  if (!sessionStorage.getItem(SESSION_START_KEY)) {
    sessionStorage.setItem(SESSION_START_KEY, now.toString());
  }
  
  // Update last activity time
  sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString());
  
  // Set up periodic check for session timeout
  startSessionTimeoutCheck();
};

/**
 * Update last activity timestamp
 * Call this on user interactions to extend session
 */
export const updateSessionActivity = () => {
  const now = Date.now();
  sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString());
};

/**
 * Check if session has expired
 * @returns {boolean} true if session has expired
 */
export const isSessionExpired = () => {
  const lastActivity = sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
  
  if (!lastActivity) {
    return true; // No activity recorded, consider expired
  }
  
  const lastActivityTime = parseInt(lastActivity, 10);
  const now = Date.now();
  
  return (now - lastActivityTime) > SESSION_TIMEOUT_DURATION;
};

/**
 * Clear all session data
 */
export const clearSessionData = () => {
  console.log('Session expired - clearing all session data');
  
  SESSION_DATA_KEYS.forEach(key => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove session key ${key}:`, error);
    }
  });
  
  // Clear timeout tracking keys as well
  sessionStorage.removeItem(SESSION_START_KEY);
  sessionStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
};

/**
 * Get remaining session time in milliseconds
 * @returns {number} remaining time in ms, or 0 if expired
 */
export const getRemainingSessionTime = () => {
  const lastActivity = sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY);
  
  if (!lastActivity) {
    return 0;
  }
  
  const lastActivityTime = parseInt(lastActivity, 10);
  const now = Date.now();
  const elapsed = now - lastActivityTime;
  
  return Math.max(0, SESSION_TIMEOUT_DURATION - elapsed);
};

/**
 * Get remaining session time in a human-readable format
 * @returns {string} formatted time remaining
 */
export const getFormattedRemainingTime = () => {
  const remaining = getRemainingSessionTime();
  
  if (remaining === 0) {
    return 'Session expired';
  }
  
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s remaining`;
  } else {
    return `${seconds}s remaining`;
  }
};

/**
 * Start periodic session timeout check
 * Checks every minute if session has expired
 */
let sessionTimeoutInterval = null;

export const startSessionTimeoutCheck = () => {
  // Clear existing interval if any
  if (sessionTimeoutInterval) {
    clearInterval(sessionTimeoutInterval);
  }
  
  // Check every minute
  sessionTimeoutInterval = setInterval(() => {
    if (isSessionExpired()) {
      clearSessionData();
      clearInterval(sessionTimeoutInterval);
      sessionTimeoutInterval = null;
      
      // Dispatch custom event to notify components
      window.dispatchEvent(new CustomEvent('sessionExpired'));
    }
  }, 60000); // Check every minute
};

/**
 * Stop session timeout check
 * Call this when user logs out or app unmounts
 */
export const stopSessionTimeoutCheck = () => {
  if (sessionTimeoutInterval) {
    clearInterval(sessionTimeoutInterval);
    sessionTimeoutInterval = null;
  }
};

/**
 * Reset session timeout
 * Call this when user performs important actions
 */
export const resetSessionTimeout = () => {
  updateSessionActivity();
  
  // Restart the timeout check
  startSessionTimeoutCheck();
};

/**
 * Check if session is about to expire (within 5 minutes)
 * @returns {boolean} true if session expires within 5 minutes
 */
export const isSessionAboutToExpire = () => {
  const remaining = getRemainingSessionTime();
  const fiveMinutes = 5 * 60 * 1000;
  return remaining > 0 && remaining <= fiveMinutes;
};
